/**
 * User service for client-side API calls
 */
import apiService from './apiService';

// Base URL for user endpoints - apiService will add the /api prefix automatically
const BASE_URL = '/users';

/**
 * Fetch users with optional pagination and search
 * @param {Object} options - Pagination options
 * @param {number} options.limit - Number of users per page
 * @param {number} options.offset - Offset for pagination
 * @param {string} options.search - Search term
 * @returns {Promise<Object>} - Users and total count
 */
const fetchUsers = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.search) params.append('search', options.search);

    const queryString = params.toString();
    const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
    return await apiService.get(url);
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Fetch a single user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User data
 */
const fetchUserById = async (userId) => {
  try {
    return await apiService.get(`${BASE_URL}/${userId}`);
  } catch (error) {
    console.error(`Error fetching user ${userId}:`, error);
    throw error;
  }
};

/**
 * Create a new user
 * @param {Object} userData - User data to create
 * @returns {Promise<Object>} - Created user data
 */
const createUser = async (userData) => {
  try {
    return await apiService.post(BASE_URL, userData);
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update an existing user
 * @param {string} userId - User ID to update
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} - Updated user data
 */
const updateUser = async (userId, userData) => {
  try {
    return await apiService.put(`${BASE_URL}/${userId}`, userData);
  } catch (error) {
    console.error(`Error updating user ${userId}:`, error);
    throw error;
  }
};

/**
 * Delete a user
 * @param {string} userId - User ID to delete
 * @returns {Promise<Object>} - Response data
 */
const deleteUser = async (userId) => {
  try {
    return await apiService.delete(`${BASE_URL}/${userId}`);
  } catch (error) {
    console.error(`Error deleting user ${userId}:`, error);
    throw error;
  }
};

/**
 * Reset a user's password to the default and require password change on next login
 * @param {string} userId - User ID to reset
 * @returns {Promise<Object>} - Response data
 */
const resetUserPassword = async (userId) => {
  try {
    return await apiService.post(`${BASE_URL}/${userId}/reset-password`);
  } catch (error) {
    console.error(`Error resetting password for user ${userId}:`, error);
    throw error;
  }
};
const resetUserPasswordAndEmail = async (userId, options = {}) => {
  try {
    return await apiService.post(`${BASE_URL}/${userId}/reset-password-email`, options);
  } catch (error) {
    console.error(`Error resetting password+email for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Fetch sites associated with a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - User's sites
 */
const fetchUserSites = async (userId) => {
  try {
    return await apiService.get(`${BASE_URL}/${userId}/sites`);
  } catch (error) {
    console.error(`Error fetching sites for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Update sites associated with a user
 * @param {string} userId - User ID
 * @param {Array} siteIds - Array of site IDs to associate
 * @returns {Promise<Object>} - Response data
 */
const updateUserSites = async (userId, siteIds) => {
  try {
    return await apiService.put(`${BASE_URL}/${userId}/sites`, { siteIds });
  } catch (error) {
    console.error(`Error updating sites for user ${userId}:`, error);
    throw error;
  }
};

const fetchUserAccounts = async (userId) => {
  try {
    return await apiService.get(`${BASE_URL}/${userId}/accounts`);
  } catch (error) {
    console.error(`Error fetching accounts for user ${userId}:`, error);
    throw error;
  }
};

const updateUserAccounts = async (userId, accountIds) => {
  try {
    return await apiService.put(`${BASE_URL}/${userId}/accounts`, { accountIds });
  } catch (error) {
    console.error(`Error updating accounts for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Fetch devices associated with a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - User's devices
 */
const fetchUserDevices = async (userId) => {
  try {
    return await apiService.get(`${BASE_URL}/${userId}/devices`);
  } catch (error) {
    console.error(`Error fetching devices for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Update device associations for a user
 * @param {string} userId - User ID
 * @param {Array} deviceIds - Array of device IDs to associate
 * @returns {Promise<Object>} - Updated associations
 */
const updateUserDevices = async (userId, deviceIds) => {
  try {
    return await apiService.put(`${BASE_URL}/${userId}/devices`, { deviceIds });
  } catch (error) {
    console.error(`Error updating user ${userId} devices:`, error);
    throw error;
  }
};

/**
 * Fetch assignable device options for user management
 * @param {Object} options - Pagination/search options
 * @returns {Promise<Object>} - { devices, total, limit, offset }
 */
const fetchDeviceOptions = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.search) params.append('search', options.search);

    const queryString = params.toString();
    const url = queryString ? `${BASE_URL}/device-options?${queryString}` : `${BASE_URL}/device-options`;
    return await apiService.get(url);
  } catch (error) {
    console.error('Error fetching user device options:', error);
    throw error;
  }
};

/**
 * Import users from CSV file
 * @param {File} csvFile - CSV file to import
 * @returns {Promise<Object>} - Import results
 */
const importUsersFromCsv = async (csvFile) => {
  try {
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    
    return await apiService.post(`${BASE_URL}/import-csv`, formData);
  } catch (error) {
    console.error('Error importing users from CSV:', error);
    throw error;
  }
};

const userService = {
  fetchUsers,
  fetchUserById,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  resetUserPasswordAndEmail,
  fetchUserSites,
  updateUserSites,
  fetchUserAccounts,
  updateUserAccounts,
  fetchUserDevices,
  updateUserDevices,
  fetchDeviceOptions,
  importUsersFromCsv
};

export default userService;
